"use client";
import { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { CreateMeetingLink, GetMeetingsLink, GetUserInfo } from '../../Api';
import { motion } from 'framer-motion';
import { gapi } from 'gapi-script';

const ModalMeeting = ({ isOpen, closeModal, userId }) => {
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [targetEmail, setTargetEmail] = useState('');
  const [getLink, setGetLink] = useState([]);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Google API credentials
  const API_KEY = 'AIzaSyCfkEq-qL2grUkDmsuJywGEvdm2FJ_z9qU';
  const CLIENT_ID = '52543362771-gmr6c410lh3rikhe74kb7cpsuvuso3i1.apps.googleusercontent.com';
  const SCOPES = 'https://www.googleapis.com/auth/calendar';

  // Initialize Google API client and fetch user info
  useEffect(() => {
    const initClient = () => {
      gapi.client
        .init({
          apiKey: API_KEY,
          clientId: CLIENT_ID,
          scope: SCOPES,
          discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
        })
        .then(() => {
          // Listen for sign-in state changes
          gapi.auth2.getAuthInstance().isSignedIn.listen(updateSigninStatus);
          // Handle the initial sign-in state
          updateSigninStatus(gapi.auth2.getAuthInstance().isSignedIn.get());
        })
        .catch((error) => {
          console.error('Error initializing Google API client:', error);
          setMessage('Failed to initialize Google API client');
        });
    };

    if (isOpen) {
      gapi.load('client:auth2', initClient);
      GetUserInfo(userId)
        .then((result) => {
          setTargetEmail(result.data.data[0].email);
        })
        .catch((error) => {
          console.error('Error fetching user info:', error);
          setMessage('Failed to fetch user email');
        });
    }
  }, [isOpen, userId]);

  const updateSigninStatus = (isUserSignedIn) => {
    setIsSignedIn(isUserSignedIn);
    if (isUserSignedIn) {
      setMessage('Google account connected successfully!');
    } else {
      setMessage('Please sign in to create a Google Meet link');
    }
  };

  const handleSignIn = () => {
    gapi.auth2.getAuthInstance().signIn();
  };

  const handleSignOut = () => {
    gapi.auth2.getAuthInstance().signOut();
    setLink('');
    setMessage('Signed out successfully');
  };

  const createMeetEvent = () => {
    if (!text) {
      setMessage('Please enter a meeting description');
      return;
    }

    if (!meetingDate) {
      setMessage('Please select a meeting date');
      return;
    }

    setIsLoading(true);
    setMessage('Creating meeting...');

    // Parse the selected date and create end time (1 hour later)
    const selectedDate = new Date(meetingDate);
    const endTime = new Date(selectedDate);
    endTime.setHours(endTime.getHours() + 1);

    // Create the calendar event with Google Meet
    const event = {
      summary: text,
      description: `Meeting created via Bring Your Best platform.`,
      start: {
        dateTime: selectedDate.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      end: {
        dateTime: endTime.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      attendees: targetEmail ? [{ email: targetEmail }] : [],
      conferenceData: {
        createRequest: {
          requestId: Date.now().toString(),
        },
      },
    };

    gapi.client.calendar.events
      .insert({
        calendarId: 'primary',
        resource: event,
        conferenceDataVersion: 1,
        sendUpdates: targetEmail ? 'all' : 'none',
      })
      .then((response) => {
        setIsLoading(false);
        console.log('Event created:', response.result);

        // Extract the Google Meet link
        if (response.result.conferenceData && response.result.conferenceData.entryPoints) {
          const meetEntryPoint = response.result.conferenceData.entryPoints.find(
            (ep) => ep.entryPointType === 'video'
          );
          if (meetEntryPoint) {
            setLink(meetEntryPoint.uri);
            setMessage('Google Meet link created successfully!');
          } else {
            setMessage('Failed to generate Google Meet link');
          }
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error('Error creating event:', error);
        setMessage('Failed to create meeting: ' + error.message);
      });
  };

  const handleCreateClick = async () => {
    if (!text || !link || !meetingDate) {
      setMessage('Meeting description, link, and date are required.');
      return;
    }

    try {
      await CreateMeetingLink(userId, text, link, new Date(meetingDate));
      const result = await GetMeetingsLink(userId);
      setGetLink(result.data);
      setMessage('Meeting created and saved successfully!');
      // Reset form fields
      setText('');
      setLink('');
      setMeetingDate('');
    } catch (error) {
      console.error('Error creating or fetching meeting link:', error);
      setMessage('Failed to save or fetch meeting');
    }
  };

  const handleCloseModal = () => {
    setText('');
    setLink('');
    setMeetingDate('');
    setTargetEmail('');
    setGetLink([]);
    setMessage('');
    closeModal();
  };

  return (
    isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 bg-black/70 flex justify-center items-center z-50"
      >
        <motion.div
          initial={{ y: '-100vh' }}
          animate={{ y: 0 }}
          exit={{ y: '-100vh' }}
          transition={{ duration: 0.3 }}
          className="bg-gradient-to-br from-gray-800 to-black p-6 rounded-lg w-full h-full max-w-none max-h-none overflow-auto"
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-white">Create Meeting Link</h2>

            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium">Meeting Description</label>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="mt-2 p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg w-full focus:outline-none focus:ring focus:ring-blue-500"
                placeholder="Enter meeting description"
              />
            </div>

            <div className="mb-6">
              <label className="flex items-center text-gray-300 text-sm font-medium">
                <Calendar size={24} className="mr-2 text-blue-400" />
                Select Date for Meeting
              </label>
              <input
                type="datetime-local"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                className="mt-2 p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg w-full focus:outline-none focus:ring focus:ring-blue-500"
              />
            </div>

            <div className="mb-6">
              <label className="block text-gray-300 text-sm font-medium">Attendee Email (Optional)</label>
              <input
                type="email"
                value={targetEmail}
                onChange={(e) => setTargetEmail(e.target.value)}
                className="mt-2 p-3 border border-gray-600 bg-gray-700 text-gray-200 rounded-lg w-full focus:outline-none focus:ring focus:ring-blue-500"
                placeholder="Enter email for invitation"
              />
            </div>

            {!isSignedIn ? (
              <div className="mb-6">
                <button
                  onClick={handleSignIn}
                  className="w-full py-3 px-6 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200"
                >
                  Sign in with Google to Create Meet Link
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6 flex gap-4">
                  <button
                    onClick={createMeetEvent}
                    disabled={isLoading}
                    className="flex-1 py-3 px-6 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-green-800 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isLoading ? 'Creating Meet Link...' : 'Generate Google Meet Link'}
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="flex-1 py-3 px-6 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200"
                  >
                    Sign Out
                  </button>
                </div>

                {link && (
                  <div className="mb-6 p-4 bg-gray-700 rounded-lg">
                    <p className="font-medium text-white mb-1">Meeting Link:</p>
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 break-all"
                    >
                      {link}
                    </a>
                  </div>
                )}
              </>
            )}

            {message && (
              <div className="mb-6 p-3 bg-blue-900/50 text-blue-200 rounded-lg">
                {message}
              </div>
            )}

            <div className="flex justify-between mt-8">
              <button
                onClick={handleCloseModal}
                className="py-3 px-6 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateClick}
                disabled={!text || !link || !meetingDate}
                className="py-3 px-6 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-blue-800 disabled:cursor-not-allowed transition-all duration-200"
              >
                Save Meeting
              </button>
            </div>

            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4 text-white">Assigned Meetings</h3>
              {getLink.length > 0 ? (
                <ul className="space-y-4">
                  {getLink.map((data) => (
                    <motion.li
                      key={data.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="flex items-center justify-between p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all duration-200"
                    >
                      <div className="flex items-center space-x-4">
                        <Calendar size={24} className="text-blue-400" />
                        <div>
                          <a
                            href={data.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 underline hover:text-blue-300"
                          >
                            {data.text}
                          </a>
                        </div>
                      </div>
                      <span className="text-sm text-gray-400">
                        {new Date(data.date).toLocaleDateString()}
                      </span>
                    </motion.li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-400 text-gray-400">No meetings assigned yet.</p>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    )
  );
};

export default ModalMeeting;